import { AvailablePaymentCurrency, formatCurrencyAmount, IsoLocale, Locales } from "@simplyk/common";

import { formatRatePpmAsPercent, sumTaxRatePpm } from "../../RateFields/TaxesField/helpers";

import { Translate } from "@/common/hooks/useTranslate";
import { isRateTaxable } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/helpers/rateTaxes";
import { RateData } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/types";

interface FormatContext {
  locale: IsoLocale | Locales;
  currency: AvailablePaymentCurrency;
  t: Translate;
  taxRatePpmById?: ReadonlyMap<string, number>;
}

export const BULLET_SEPARATOR = "\u2002\u2022\u2002";

const EMPTY_TAX_RATE_MAP: ReadonlyMap<string, number> = new Map();

export const formatAuctionIdleSummary = (
  rate: Partial<RateData>,
  { locale, currency, t }: FormatContext
): string | null => {
  const parts: string[] = [];

  if (typeof rate.amount === "number" && rate.amount > 0) {
    parts.push(
      t("dashboard", "ticketing.idleSummary.startingBid", {
        amount: formatCurrencyAmount({ value: rate.amount / 100, locale, currency }),
      })
    );
  }
  if (typeof rate.bidIncrement === "number" && rate.bidIncrement > 0) {
    parts.push(
      t("dashboard", "ticketing.idleSummary.minBidIncrement", {
        amount: formatCurrencyAmount({ value: rate.bidIncrement / 100, locale, currency }),
      })
    );
  }

  return parts.length ? parts.join(BULLET_SEPARATOR) : null;
};

export const formatShopIdleSummary = (
  rate: Partial<RateData>,
  { locale, currency, t, taxRatePpmById }: FormatContext
): string | null => {
  const parts: string[] = [];

  if (typeof rate.amount === "number" && rate.amount > 0) {
    parts.push(
      t("dashboard", "ticketing.idleSummary.price", {
        amount: formatCurrencyAmount({ value: rate.amount / 100, locale, currency }),
      })
    );
  }

  const taxRatePpm = isRateTaxable(rate) ? sumTaxRatePpm(rate.taxIds, taxRatePpmById ?? EMPTY_TAX_RATE_MAP) : 0;
  if (taxRatePpm > 0) {
    parts.push(t("dashboard", "ticketing.idleSummary.tax", { percent: formatRatePpmAsPercent(taxRatePpm, locale) }));
  }

  const variantValuesCount = rate.shopVariants?.reduce((sum, variant) => sum + (variant.values?.length ?? 0), 0) ?? 0;
  if (variantValuesCount > 0) {
    parts.push(t("dashboard", "ticketing.idleSummary.variants", { count: variantValuesCount }));
  }

  return parts.length ? parts.join(BULLET_SEPARATOR) : null;
};

export const formatCustomIdleSummary = (
  rate: Partial<RateData>,
  { locale, currency, t, taxRatePpmById }: FormatContext
): string | null => {
  const parts: string[] = [];

  if (typeof rate.amount === "number" && rate.amount > 0) {
    parts.push(
      t("dashboard", "ticketing.idleSummary.price", {
        amount: formatCurrencyAmount({ value: rate.amount / 100, locale, currency }),
      })
    );
  }

  const taxRatePpm = isRateTaxable(rate) ? sumTaxRatePpm(rate.taxIds, taxRatePpmById ?? EMPTY_TAX_RATE_MAP) : 0;
  if (taxRatePpm > 0) {
    parts.push(t("dashboard", "ticketing.idleSummary.tax", { percent: formatRatePpmAsPercent(taxRatePpm, locale) }));
  }

  return parts.length ? parts.join(BULLET_SEPARATOR) : null;
};

export const getIdleThumbnailUrl = (rate: Partial<RateData>): string | null => {
  const photo = rate.photos?.find((p) => Boolean(p.photoUrl));
  return photo?.photoUrl ?? null;
};
